import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import chroma from 'chroma-js';

const PageContainer = styled.div`
  padding: 20px;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  margin-top: 80px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Button = styled.button`
  background-color: #62E07D;
  color: black;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;

  &:hover {
    background-color: #50B366;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
`;

const ViewButton = styled.button`
  background: ${props => props.active ? '#62E07D' : 'transparent'};
  color: ${props => props.active ? 'black' : 'white'};
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#62E07D' : 'rgba(98, 224, 125, 0.2)'};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  text-transform: uppercase;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 2px solid ${props => props.theme.colors.border};
  font-weight: 600;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  user-select: none;
  background: ${props => props.active ? 'rgba(98, 224, 125, 0.1)' : 'transparent'};

  &:hover {
    background: ${props => props.sortable ? 'rgba(98, 224, 125, 0.05)' : 'transparent'};
  }
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Tr = styled.tr`
  &:hover {
    background: rgba(98, 224, 125, 0.05);
  }
`;

const ColorSwatch = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background-color: ${props => props.color};
  border: 2px solid rgba(255, 255, 255, 0.2);
`;

const EditButton = styled.button`
  background: transparent;
  color: #62E07D;
  border: 1px solid #62E07D;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 11px;
  margin-right: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #62E07D;
    color: black;
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  color: #FF6B6B;
  border: 1px solid #FF6B6B;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 11px;
  transition: all 0.2s ease;

  &:hover {
    background: #FF6B6B;
    color: white;
  }
`;

const OrderButton = styled.a`
  background: transparent;
  color: #FFD700;
  border: 1px solid #FFD700;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 11px;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
  margin-right: 8px;

  &:hover {
    background: #FFD700;
    color: black;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  padding: 30px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  color: white;

  h2 {
    margin-top: 0;
    color: #62E07D;
    font-family: 'Montserrat', sans-serif;
    text-transform: uppercase;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #62E07D;
  }
`;

const Checkbox = styled.input`
  margin-right: 8px;
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SaveButton = styled(Button)``;

const CancelButton = styled.button`
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;

  &:focus {
    outline: none;
    border-color: #62E07D;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const StockBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: ${props => props.inStock ? 'rgba(98, 224, 125, 0.2)' : 'rgba(255, 107, 107, 0.2)'};
  color: ${props => props.inStock ? '#62E07D' : '#FF6B6B'};
  border: 1px solid ${props => props.inStock ? '#62E07D' : '#FF6B6B'};
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#62E07D' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.active ? 'black' : 'white'};
  border: 1px solid ${props => props.active ? '#62E07D' : 'rgba(255, 255, 255, 0.2)'};
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#50B366' : 'rgba(98, 224, 125, 0.2)'};
  }
`;

// Matcher Components
const MatcherContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const MatcherInputSection = styled.div`
  width: 100%;
  max-width: 600px;
  margin-bottom: 40px;
`;

const MatcherTitle = styled.h2`
  color: #62E07D;
  margin-bottom: 24px;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
`;

const HexInputGroup = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
`;

const HexInputWrapper = styled.div`
  flex: 1;
`;

const ColorPreview = styled.div`
  width: 60px;
  height: 36px;
  border-radius: 4px;
  background-color: ${props => props.color};
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`;

const EyedropperButton = styled.button`
  background: rgba(98, 224, 125, 0.2);
  color: #62E07D;
  border: 1px solid #62E07D;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(98, 224, 125, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const MatchSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 32px;
  background: ${props => props.highlight ? 'rgba(98, 224, 125, 0.05)' : 'rgba(255, 255, 255, 0.03)'};
  border: 2px solid ${props => props.highlight ? '#62E07D' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 24px;
`;

const MatchSectionTitle = styled.h3`
  color: ${props => props.color || 'white'};
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MatchResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  width: 100%;
`;

const MatchCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid ${props => {
    if (props.deltaE < 1) return '#62E07D';
    if (props.deltaE < 5) return '#62E07D';
    if (props.deltaE < 15) return '#FFD700';
    if (props.deltaE < 30) return '#FFA500';
    return '#FF6B6B';
  }};
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MatchCardHeader = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const MatchColorSwatch = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background-color: ${props => props.color};
  border: 2px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`;

const MatchInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MatchName = styled.div`
  color: white;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MatchDetails = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
`;

const DeltaEBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  background: ${props => {
    if (props.value < 1) return 'rgba(98, 224, 125, 0.2)';
    if (props.value < 5) return 'rgba(98, 224, 125, 0.2)';
    if (props.value < 15) return 'rgba(255, 215, 0, 0.2)';
    if (props.value < 30) return 'rgba(255, 165, 0, 0.2)';
    return 'rgba(255, 107, 107, 0.2)';
  }};
  color: ${props => {
    if (props.value < 1) return '#62E07D';
    if (props.value < 5) return '#62E07D';
    if (props.value < 15) return '#FFD700';
    if (props.value < 30) return '#FFA500';
    return '#FF6B6B';
  }};
  margin-bottom: 8px;
`;

const MatchQuality = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  font-weight: 600;
`;

const NoResults = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  padding: 60px 20px;
  font-size: 14px;
`;

const BigMatchCard = styled(MatchCard)`
  background: ${props => props.isInStock ? 'rgba(98, 224, 125, 0.1)' : 'rgba(98, 224, 255, 0.1)'};
  border-width: 3px;
  padding: 20px;
`;

const BigMatchCardHeader = styled(MatchCardHeader)`
  margin-bottom: 16px;
`;

const BigMatchColorSwatch = styled(MatchColorSwatch)`
  width: 80px;
  height: 80px;
`;

const BigMatchName = styled(MatchName)`
  font-size: 18px;
`;

const BigDeltaEBadge = styled(DeltaEBadge)`
  font-size: 16px;
  padding: 8px 16px;
`;

// Thread Matcher Component
const ThreadMatcher = ({ threads, onThreadClick }) => {
  const [inputValue, setInputValue] = useState('');
  const [targetHex, setTargetHex] = useState('');
  const [matches, setMatches] = useState([]);
  const [bestInStock, setBestInStock] = useState(null);
  const [bestOverall, setBestOverall] = useState(null);

  // Handle input change (hex only for threads - no Pantone for threads)
  const handleInputChange = (value) => {
    setInputValue(value);
    let normalized = value.trim();
    if (normalized && !normalized.startsWith('#')) {
      normalized = '#' + normalized;
    }
    setTargetHex(normalized);
  };

  // Eyedropper functionality
  const handleEyedropper = async () => {
    if (!window.EyeDropper) {
      alert('Color picker is not supported in your browser. Please use Chrome, Edge, or another Chromium-based browser.');
      return;
    }

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      const hexColor = result.sRGBHex.toUpperCase();
      setInputValue(hexColor);
      setTargetHex(hexColor);
    } catch (error) {
      // User cancelled or error occurred
      console.log('Eyedropper cancelled or error:', error);
    }
  };

  // Calculate Delta E
  const calculateDeltaE = (hex1, hex2) => {
    try {
      const color1 = chroma(hex1);
      const color2 = chroma(hex2);
      return chroma.deltaE(color1, color2);
    } catch (error) {
      return 999;
    }
  };

  // Get quality label
  const getQualityLabel = (deltaE) => {
    if (deltaE < 1) return 'Perfect Match';
    if (deltaE < 5) return 'Excellent Match';
    if (deltaE < 15) return 'Good Match';
    if (deltaE < 30) return 'Moderate Match';
    return 'Poor Match';
  };

  // Calculate matches
  useEffect(() => {
    if (!targetHex || targetHex.length < 4) {
      setMatches([]);
      setBestInStock(null);
      setBestOverall(null);
      return;
    }

    try {
      chroma(targetHex);
      
      // Calculate Delta E for all threads with hex codes
      const validThreads = threads.filter(thread => thread.hexCode);
      const matchesWithDeltaE = validThreads.map(thread => ({
        ...thread,
        deltaE: calculateDeltaE(targetHex, thread.hexCode)
      }));

      // Sort by Delta E
      matchesWithDeltaE.sort((a, b) => a.deltaE - b.deltaE);

      // Find best in-stock
      const inStockMatches = matchesWithDeltaE.filter(t => t.inStock);
      setBestInStock(inStockMatches[0] || null);

      // Best overall
      setBestOverall(matchesWithDeltaE[0] || null);

      // Top 12 matches
      setMatches(matchesWithDeltaE.slice(0, 12));
    } catch (error) {
      setMatches([]);
      setBestInStock(null);
      setBestOverall(null);
    }
  }, [targetHex, threads]);

  const isValidHex = (hex) => {
    try {
      chroma(hex);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <MatcherContainer>
      <MatcherInputSection>
        <MatcherTitle>Thread Color Matcher</MatcherTitle>
        <HexInputGroup>
          <HexInputWrapper>
            <Label>Enter Target Hex Code or Use Color Picker</Label>
            <Input
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="#FF6B35 or FF6B35"
              style={{ textTransform: 'uppercase', fontSize: '18px' }}
            />
          </HexInputWrapper>
          <EyedropperButton 
            onClick={handleEyedropper}
            title="Pick a color from anywhere on your screen"
            disabled={!window.EyeDropper}
          >
            ⊙ Pick
          </EyedropperButton>
          <ColorPreview color={isValidHex(targetHex) ? targetHex : '#333'} />
        </HexInputGroup>
        
        {inputValue && !isValidHex(targetHex) && (
          <div style={{ 
            color: '#FF6B6B', 
            fontSize: '12px', 
            marginTop: '12px',
            fontFamily: 'Montserrat',
            textTransform: 'uppercase'
          }}>
            Invalid hex code. Use format: #RRGGBB
          </div>
        )}
      </MatcherInputSection>

      {matches.length > 0 ? (
        <>
          {/* Best In-Stock Match */}
          {bestInStock && (
            <MatchSection highlight={true}>
              <MatchSectionTitle color="#62E07D">
                🟢 CLOSEST IN-STOCK MATCH
              </MatchSectionTitle>
              <BigMatchCard deltaE={bestInStock.deltaE} isInStock={true}>
                <BigMatchCardHeader>
                  <BigMatchColorSwatch color={bestInStock.hexCode} />
                  <MatchInfo>
                    <BigMatchName>Thread {bestInStock.threadNumber}</BigMatchName>
                    <MatchDetails>
                      {bestInStock.hexCode}
                      {bestInStock.name && <div style={{ marginTop: '4px' }}>{bestInStock.name}</div>}
                      {bestInStock.quantity && <div style={{ marginTop: '4px', color: '#62E07D' }}>
                        {bestInStock.quantity} spool{bestInStock.quantity !== 1 ? 's' : ''} in stock
                      </div>}
                    </MatchDetails>
                  </MatchInfo>
                </BigMatchCardHeader>
                <BigDeltaEBadge value={bestInStock.deltaE}>
                  ΔE: {bestInStock.deltaE.toFixed(1)}
                </BigDeltaEBadge>
                <MatchQuality deltaE={bestInStock.deltaE}>
                  {getQualityLabel(bestInStock.deltaE)}
                </MatchQuality>
              </BigMatchCard>
            </MatchSection>
          )}

          {/* Best Overall Match (if different from in-stock) */}
          {bestOverall && (!bestInStock || bestOverall.id !== bestInStock.id) && (
            <MatchSection>
              <MatchSectionTitle color="#62C0E0">
                🔵 ACTUAL CLOSEST MATCH {!bestOverall.inStock && '⚠️ NOT IN STOCK'}
              </MatchSectionTitle>
              <BigMatchCard deltaE={bestOverall.deltaE} isInStock={false}>
                <BigMatchCardHeader>
                  <BigMatchColorSwatch color={bestOverall.hexCode} />
                  <MatchInfo>
                    <BigMatchName>Thread {bestOverall.threadNumber}</BigMatchName>
                    <MatchDetails>
                      {bestOverall.hexCode}
                      {bestOverall.name && <div style={{ marginTop: '4px' }}>{bestOverall.name}</div>}
                      <div style={{ marginTop: '4px', color: '#FF6B6B' }}>
                        Not in stock - needs ordering
                      </div>
                      <div style={{ marginTop: '12px' }}>
                        <OrderButton 
                          href={`https://www.madeirausa.com/Search/Default.aspx?Keywords=${bestOverall.threadNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Order Now →
                        </OrderButton>
                      </div>
                    </MatchDetails>
                  </MatchInfo>
                </BigMatchCardHeader>
                <BigDeltaEBadge value={bestOverall.deltaE}>
                  ΔE: {bestOverall.deltaE.toFixed(1)}
                </BigDeltaEBadge>
                <MatchQuality deltaE={bestOverall.deltaE}>
                  {getQualityLabel(bestOverall.deltaE)}
                </MatchQuality>
              </BigMatchCard>
            </MatchSection>
          )}

          {/* Top 12 Matches */}
          <MatchSection>
            <MatchSectionTitle>Top {matches.length} Overall Matches</MatchSectionTitle>
            <MatchResultsGrid>
              {matches.map(thread => (
                <MatchCard 
                  key={thread.id} 
                  deltaE={thread.deltaE}
                  onClick={() => onThreadClick(thread)}
                >
                  <MatchCardHeader>
                    <MatchColorSwatch color={thread.hexCode} />
                    <MatchInfo>
                      <MatchName>Thread {thread.threadNumber}</MatchName>
                      <MatchDetails>
                        {thread.hexCode}
                        {thread.name && <div style={{ marginTop: '4px' }}>{thread.name}</div>}
                        <div style={{ marginTop: '4px' }}>
                          <StockBadge inStock={thread.inStock}>
                            {thread.inStock ? '✓ In Stock' : 'Need to Order'}
                          </StockBadge>
                          {!thread.inStock && (
                            <div style={{ marginTop: '8px' }}>
                              <OrderButton 
                                href={`https://www.madeirausa.com/Search/Default.aspx?Keywords=${thread.threadNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Order Now
                              </OrderButton>
                            </div>
                          )}
                        </div>
                      </MatchDetails>
                    </MatchInfo>
                  </MatchCardHeader>
                  <DeltaEBadge value={thread.deltaE}>
                    ΔE: {thread.deltaE.toFixed(1)}
                  </DeltaEBadge>
                  <MatchQuality deltaE={thread.deltaE}>
                    {getQualityLabel(thread.deltaE)}
                  </MatchQuality>
                </MatchCard>
              ))}
            </MatchResultsGrid>
          </MatchSection>

          {/* Delta E Guide */}
          <div style={{
            marginTop: '40px',
            padding: '20px',
            background: 'rgba(98, 224, 125, 0.1)',
            border: '1px solid rgba(98, 224, 125, 0.3)',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '100%'
          }}>
            <div style={{
              color: 'white',
              fontFamily: 'Montserrat',
              fontSize: '12px',
              textTransform: 'uppercase',
              fontWeight: 600,
              marginBottom: '12px'
            }}>
              Delta E (ΔE) Scale Guide
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.7)',
              fontFamily: 'Montserrat',
              fontSize: '11px',
              textTransform: 'uppercase',
              lineHeight: 1.8
            }}>
              <div>• <strong style={{ color: '#62E07D' }}>ΔE &lt; 1:</strong> Perfect Match (imperceptible difference)</div>
              <div>• <strong style={{ color: '#62E07D' }}>ΔE 1-5:</strong> Excellent Match (slight difference, very close)</div>
              <div>• <strong style={{ color: '#FFD700' }}>ΔE 5-15:</strong> Good Match (noticeable but acceptable)</div>
              <div>• <strong style={{ color: '#FFA500' }}>ΔE 15-30:</strong> Moderate Match (clearly different)</div>
              <div>• <strong style={{ color: '#FF6B6B' }}>ΔE &gt; 30:</strong> Poor Match (significantly different)</div>
            </div>
          </div>
        </>
      ) : (
        <NoResults>
          {inputValue 
            ? (isValidHex(targetHex) ? 'No threads with color data found' : 'Enter a valid hex code to find matches')
            : 'Enter a hex code above to find closest matching threads'}
        </NoResults>
      )}
    </MatcherContainer>
  );
};

function ThreadManager() {
  const [threads, setThreads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingThread, setEditingThread] = useState(null);
  const [editForm, setEditForm] = useState({ 
    threadNumber: '', 
    hexCode: '', 
    name: '', 
    inStock: false,
    quantity: '',
    location: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [sortColumn, setSortColumn] = useState('threadNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'matcher'
  const [stockFilter, setStockFilter] = useState('all'); // 'all', 'inStock', 'outOfStock'

  // Sort function
  const sortThreads = (threadsToSort, column, direction) => {
    const sorted = [...threadsToSort].sort((a, b) => {
      let aVal, bVal;
      
      switch (column) {
        case 'threadNumber':
          aVal = a.threadNumber || '';
          bVal = b.threadNumber || '';
          return direction === 'asc' 
            ? aVal.localeCompare(bVal, undefined, { numeric: true })
            : bVal.localeCompare(aVal, undefined, { numeric: true });
          
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          return direction === 'asc' 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
          
        case 'inStock':
          aVal = a.inStock ? 1 : 0;
          bVal = b.inStock ? 1 : 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
          
        default:
          return 0;
      }
    });
    
    return sorted;
  };

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter and sort threads
  const filteredThreads = useMemo(() => {
    let filtered = threads.filter(thread => 
      (thread.threadNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       thread.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Apply stock filter
    if (stockFilter === 'inStock') {
      filtered = filtered.filter(t => t.inStock);
    } else if (stockFilter === 'outOfStock') {
      filtered = filtered.filter(t => !t.inStock);
    }

    return sortThreads(filtered, sortColumn, sortDirection);
  }, [threads, searchTerm, sortColumn, sortDirection, stockFilter]);

  // Fetch threads from Firestore
  const fetchThreads = async () => {
    try {
      setIsLoading(true);
      const threadsRef = collection(db, 'threads');
      const snapshot = await getDocs(threadsRef);
      const threadsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      threadsList.sort((a, b) => (a.threadNumber || '').localeCompare(b.threadNumber || '', undefined, { numeric: true }));
      setThreads(threadsList);
    } catch (error) {
      console.error('Error fetching threads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchThreads();
  }, []);

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingThread(null);
    setEditForm({ threadNumber: '', hexCode: '', name: '', inStock: false, quantity: '', location: '' });
  };

  const handleEdit = (thread) => {
    setEditingThread(thread);
    setIsAddingNew(false);
    setEditForm({
      threadNumber: thread.threadNumber || '',
      hexCode: thread.hexCode || '',
      name: thread.name || '',
      inStock: thread.inStock || false,
      quantity: thread.quantity || '',
      location: thread.location || ''
    });
  };

  const handleSave = async () => {
    if (!editForm.threadNumber || editForm.threadNumber.trim() === '') {
      alert('Thread number is required');
      return;
    }

    // Validate hex code if provided
    const hexCode = editForm.hexCode?.trim() || '';
    if (hexCode && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexCode)) {
      alert('Invalid hex code format. Please use format #RRGGBB or #RGB');
      return;
    }

    try {
      const updateData = {
        threadNumber: editForm.threadNumber.trim(),
        hexCode: hexCode || null,
        name: editForm.name?.trim() || null,
        inStock: editForm.inStock,
        quantity: editForm.quantity ? parseInt(editForm.quantity) : null,
        location: editForm.location?.trim() || null,
        updatedAt: serverTimestamp()
      };

      if (editingThread) {
        const threadRef = doc(db, 'threads', editingThread.id);
        await updateDoc(threadRef, updateData);
      } else {
        await addDoc(collection(db, 'threads'), {
          ...updateData,
          createdAt: serverTimestamp()
        });
      }
      
      await fetchThreads();
      setEditingThread(null);
      setIsAddingNew(false);
      setEditForm({ threadNumber: '', hexCode: '', name: '', inStock: false, quantity: '', location: '' });
    } catch (error) {
      console.error('Error saving thread:', error);
      alert('Error saving thread. Please try again.');
    }
  };

  const handleDelete = async (threadId) => {
    if (!window.confirm('Are you sure you want to delete this thread?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'threads', threadId));
      await fetchThreads();
    } catch (error) {
      console.error('Error deleting thread:', error);
      alert('Error deleting thread. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setEditingThread(null);
    setIsAddingNew(false);
    setEditForm({ threadNumber: '', hexCode: '', name: '', inStock: false, quantity: '', location: '' });
  };

  const normalizeHexCode = (hex) => {
    if (!hex) return '';
    let normalized = hex.trim();
    if (!normalized.startsWith('#')) {
      normalized = '#' + normalized;
    }
    if (normalized.length === 4) {
      normalized = '#' + normalized[1] + normalized[1] + normalized[2] + normalized[2] + normalized[3] + normalized[3];
    }
    return normalized.toUpperCase();
  };

  const getDisplayColor = (hexCode) => {
    if (!hexCode) return '#cccccc';
    const normalized = normalizeHexCode(hexCode);
    return normalized.length === 7 ? normalized : '#cccccc';
  };

  // Eyedropper functionality for modal
  const handleModalEyedropper = async () => {
    if (!window.EyeDropper) {
      alert('Color picker is not supported in your browser. Please use Chrome, Edge, or another Chromium-based browser.');
      return;
    }

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      const hexColor = result.sRGBHex.toUpperCase();
      setEditForm({...editForm, hexCode: hexColor});
    } catch (error) {
      // User cancelled or error occurred
      console.log('Eyedropper cancelled or error:', error);
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Thread Manager</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <ViewToggle>
            <ViewButton 
              active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              📋 List
            </ViewButton>
            <ViewButton 
              active={viewMode === 'matcher'} 
              onClick={() => setViewMode('matcher')}
            >
              🔍 Matcher
            </ViewButton>
          </ViewToggle>
          <Button onClick={handleAddNew}>
            Add New Thread
          </Button>
        </div>
      </Header>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="SEARCH THREADS..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {viewMode === 'list' && (
          <>
            <FilterButton 
              active={stockFilter === 'all'}
              onClick={() => setStockFilter('all')}
            >
              All
            </FilterButton>
            <FilterButton 
              active={stockFilter === 'inStock'}
              onClick={() => setStockFilter('inStock')}
            >
              In Stock
            </FilterButton>
            <FilterButton 
              active={stockFilter === 'outOfStock'}
              onClick={() => setStockFilter('outOfStock')}
            >
              Out of Stock
            </FilterButton>
          </>
        )}
      </FiltersContainer>

      {isLoading ? (
        <div>Loading threads...</div>
      ) : viewMode === 'matcher' ? (
        <ThreadMatcher 
          threads={threads}
          onThreadClick={handleEdit}
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Color</Th>
              <Th 
                sortable 
                active={sortColumn === 'threadNumber'}
                onClick={() => handleSort('threadNumber')}
              >
                Thread # {sortColumn === 'threadNumber' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th 
                sortable 
                active={sortColumn === 'name'}
                onClick={() => handleSort('name')}
              >
                Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th>Hex Code</Th>
              <Th 
                sortable 
                active={sortColumn === 'inStock'}
                onClick={() => handleSort('inStock')}
              >
                Stock {sortColumn === 'inStock' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th>Quantity</Th>
              <Th>Location</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredThreads.length === 0 ? (
              <tr>
                <Td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                  {searchTerm || stockFilter !== 'all' 
                    ? 'No threads found matching your filters' 
                    : 'No threads in inventory. Click "Add New Thread" to get started.'}
                </Td>
              </tr>
            ) : (
              filteredThreads.map((thread) => (
                <Tr key={thread.id}>
                  <Td>
                    <ColorSwatch color={getDisplayColor(thread.hexCode)} />
                  </Td>
                  <Td>{thread.threadNumber}</Td>
                  <Td>{thread.name || '-'}</Td>
                  <Td>{thread.hexCode || '-'}</Td>
                  <Td>
                    <StockBadge inStock={thread.inStock}>
                      {thread.inStock ? '✓ In Stock' : 'Out of Stock'}
                    </StockBadge>
                  </Td>
                  <Td>{thread.quantity || '-'}</Td>
                  <Td>{thread.location || '-'}</Td>
                  <Td>
                    {!thread.inStock && (
                      <OrderButton 
                        href={`https://www.madeirausa.com/Search/Default.aspx?Keywords=${thread.threadNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Order Now
                      </OrderButton>
                    )}
                    <EditButton onClick={() => handleEdit(thread)}>
                      Edit
                    </EditButton>
                    <DeleteButton onClick={() => handleDelete(thread.id)}>
                      Delete
                    </DeleteButton>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {(editingThread || isAddingNew) && (
        <Modal onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2>{isAddingNew ? 'Add New Thread' : 'Edit Thread'}</h2>
            
            <FormGroup>
              <Label>Thread Number (e.g., 1557) *</Label>
              <Input
                value={editForm.threadNumber}
                onChange={(e) => setEditForm({...editForm, threadNumber: e.target.value})}
                placeholder="1557"
                autoFocus
              />
            </FormGroup>

            <FormGroup>
              <Label>Name (Optional)</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                placeholder="e.g., Dark Taupe"
              />
            </FormGroup>

            <FormGroup>
              <Label>Hex Code</Label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <Input
                    value={editForm.hexCode}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value && !value.startsWith('#') && /^[0-9A-Fa-f]+$/.test(value.replace('#', ''))) {
                        value = '#' + value.replace('#', '');
                      }
                      setEditForm({...editForm, hexCode: value});
                    }}
                    placeholder="#FF0000 or FF0000"
                    maxLength={7}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <EyedropperButton 
                  onClick={handleModalEyedropper}
                  title="Pick a color from anywhere on your screen"
                  disabled={!window.EyeDropper}
                  style={{ marginTop: '0' }}
                >
                  ⊙ Pick
                </EyedropperButton>
              </div>
              {editForm.hexCode && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <ColorSwatch color={getDisplayColor(editForm.hexCode)} style={{ width: '60px', height: '60px' }} />
                  <span style={{ color: '#fff', fontSize: '14px' }}>
                    {normalizeHexCode(editForm.hexCode) || editForm.hexCode}
                  </span>
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={editForm.inStock}
                  onChange={(e) => setEditForm({...editForm, inStock: e.target.checked})}
                />
                In Stock
              </CheckboxLabel>
            </FormGroup>

            {editForm.inStock && (
              <>
                <FormGroup>
                  <Label>Quantity (Number of Spools)</Label>
                  <Input
                    type="number"
                    value={editForm.quantity}
                    onChange={(e) => setEditForm({...editForm, quantity: e.target.value})}
                    placeholder="5"
                    min="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Storage Location</Label>
                  <Input
                    value={editForm.location}
                    onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                    placeholder="e.g., Shelf A-3"
                  />
                </FormGroup>
              </>
            )}

            <ButtonGroup>
              <CancelButton onClick={handleCloseModal}>Cancel</CancelButton>
              <SaveButton onClick={handleSave}>Save</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
}

export default ThreadManager;

